import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { GitBranch, Download, ChevronRight, ChevronUp, ChevronDown, Star, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Curved SVG connector element — draws smooth bezier lines from a
 *  parent pill's right-center to the left-center of each of its direct
 *  children (NotebookLM style). Measures positions after every render
 *  via useLayoutEffect + ResizeObserver so the paths stay accurate when
 *  the tree expands/collapses. */
const HConnector = ({ containerRef, tick }) => {
    const [paths, setPaths] = useState([]);
    const [dims, setDims] = useState({ w: 0, h: 0 });
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        // Parent pill lives either directly under the node (legacy) or
        // inside the `.lh-c2-mm-pill-wrap` we added so the ▲▼ arrows can
        // sit next to it. Same story for each child pill. Support both
        // shapes so the SVG connectors keep drawing after the wrap.
        const parentPill =
            container.parentElement?.querySelector(":scope > .lh-c2-mm-h-pill") ||
            container.parentElement?.querySelector(":scope > .lh-c2-mm-pill-wrap > .lh-c2-mm-h-pill");
        if (!parentPill) return;
        const childPills = Array.from(container.querySelectorAll(
            ":scope > .lh-c2-mm-h-node > .lh-c2-mm-h-pill, :scope > .lh-c2-mm-h-node > .lh-c2-mm-pill-wrap > .lh-c2-mm-h-pill"
        ));
        const compute = () => {
            const pRect = parentPill.getBoundingClientRect();
            const cRect = container.getBoundingClientRect();
            const originX = pRect.right - cRect.left;
            const originY = pRect.top + pRect.height / 2 - cRect.top;
            const w = cRect.width;
            const h = cRect.height;
            const next = childPills.map((el) => {
                const r = el.getBoundingClientRect();
                const x = r.left - cRect.left;
                const y = r.top + r.height / 2 - cRect.top;
                const midX = (originX + x) / 2;
                return `M ${originX} ${originY} C ${midX} ${originY}, ${midX} ${y}, ${x} ${y}`;
            });
            setPaths(next);
            setDims({ w, h });
        };
        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(container);
        ro.observe(parentPill);
        childPills.forEach((el) => ro.observe(el));
        window.addEventListener("resize", compute);
        return () => { ro.disconnect(); window.removeEventListener("resize", compute); };
    }, [tick, containerRef]);
    return (
        <svg className="lh-c2-mm-h-svg" width={dims.w} height={dims.h} aria-hidden>
            {paths.map((d, i) => (
                <path key={i} d={d} stroke="rgba(255,255,255,0.32)" strokeWidth="1.5" fill="none" />
            ))}
        </svg>
    );
};

/** Wrapper that lays out a parent pill horizontally next to a curved
 *   SVG connector and its stacked children column. */
const HChildren = ({ children, tick }) => {
    const ref = useRef(null);
    return (
        <div className="lh-c2-mm-h-children" ref={ref}>
            <HConnector containerRef={ref} tick={tick} />
            {children}
        </div>
    );
};

/** Concept-2 mind map — horizontal (NotebookLM-style) tree of
 *  Department → Collection → Folder → Resource. Starts fully collapsed;
 *  each node expands on click. Curved SVG connectors are drawn from
 *  each parent pill's right-edge to every direct child's left-edge.
 *
 *  Interactivity contract (added Feb 29, 2026):
 *  - Collection / folder pill body click → expand/collapse (unchanged).
 *  - `↗` icon inside each collection / folder pill → open the parent
 *    Concept2Modal via `onOpenCollection(c)`.
 *  - Resource pill click → open Concept2Viewer via `onOpenResource(r, c)`.
 *  - ▲ / ▼ arrow buttons on every reorderable pill → reorder siblings;
 *    persists via the same endpoints the grid view uses, so grid + mind
 *    map stay in sync automatically. */
const Concept2MindMap = ({ collections, depts, defaultDept = null, myDeptId = null, onOpenCollection, onOpenResource, onReorderCollections, canManageCollection }) => {
    const [deptId, setDeptId] = useState(defaultDept);
    const [expanded, setExpanded] = useState({});
    // `tick` bumps whenever the tree layout changes so HConnector
    // components re-measure and repaint their SVG paths. It has to fire
    // for BOTH triggers: expand-toggle (children appear/hide) AND the
    // async `details` fetch resolving (children materialise a beat after
    // the initial expand). Without the `details` dep, connectors were
    // measured against zero child pills on first expand and only caught
    // up when a later click bumped `tick`.
    const [tick, setTick] = useState(0);
    useEffect(() => { setTick((t) => t + 1); }, [expanded]);

    /* Default = one-step expanded — every department pill is open on
     * first render so users see the top-level collections without
     * clicking. Deeper levels stay collapsed until the user drills in. */
    useEffect(() => {
        const scope = deptId ? [deptId] : Array.from(new Set(collections.map((c) => c.owner_department_id || "u")));
        setExpanded((prev) => {
            const next = { ...prev };
            scope.forEach((did) => { next[`d-${did || "u"}`] = true; });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deptId, collections.length]);
    const [details, setDetails] = useState({}); // { collectionId: { folders, resources } }
    const canvasRef = useRef(null);
    // Re-tick whenever a bundle is fetched or mutated so HConnector
    // re-measures once the async children mount into the DOM.
    useEffect(() => { setTick((t) => t + 1); }, [details]);

    const scopedColls = useMemo(() => (
        deptId ? collections.filter((c) => c.owner_department_id === deptId) : collections
    ), [collections, deptId]);

    const scopedDeptName = useMemo(() => {
        const d = (depts || []).find((x) => x.id === deptId);
        return d?.name || "All departments";
    }, [depts, deptId]);

    const loadCollection = async (cid) => {
        if (details[cid]) return null;
        try {
            const [rr, ff] = await Promise.all([
                api.get(`/learning/collections/${cid}/resources`),
                api.get(`/learning/collections/${cid}/folders`),
            ]);
            const bundle = { resources: rr.data || [], folders: ff.data || [] };
            setDetails((d) => ({ ...d, [cid]: bundle }));
            return bundle;
        } catch { return null; }
    };

    /* Sibling reorder inside a collection bundle — persists via the same
     * endpoints the modal uses so the grid view + expanded mind-map
     * bundle stay in sync. Optimistic: reshuffle the local bundle, then
     * fire the PUT; on failure reload the bundle to snap back. */
    const reorderFoldersInBundle = async (cid, parentId, orderedIds) => {
        setDetails((d) => {
            const b = d[cid];
            if (!b) return d;
            const scoped = b.folders.filter((f) => (f.parent_folder_id || null) === (parentId || null));
            const others = b.folders.filter((f) => (f.parent_folder_id || null) !== (parentId || null));
            const byId = new Map(scoped.map((f) => [f.id, f]));
            const reordered = orderedIds.map((id) => byId.get(id)).filter(Boolean).map((f, i) => ({ ...f, order: i }));
            return { ...d, [cid]: { ...b, folders: [...others, ...reordered] } };
        });
        try {
            await api.put(`/learning/collections/${cid}/folders/reorder`, {
                folder_ids: orderedIds,
                parent_folder_id: parentId || null,
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reorder failed");
            // Force-refetch this bundle to snap back to the server truth.
            setDetails((d) => { const n = { ...d }; delete n[cid]; return n; });
            await loadCollection(cid);
        }
    };
    const reorderResourcesInBundle = async (cid, folderId, orderedIds) => {
        setDetails((d) => {
            const b = d[cid];
            if (!b) return d;
            const scoped = b.resources.filter((r) => (r.folder_id || null) === (folderId || null));
            const others = b.resources.filter((r) => (r.folder_id || null) !== (folderId || null));
            const byId = new Map(scoped.map((r) => [r.id, r]));
            const reordered = orderedIds.map((id) => byId.get(id)).filter(Boolean).map((r, i) => ({ ...r, order: i }));
            return { ...d, [cid]: { ...b, resources: [...others, ...reordered] } };
        });
        try {
            await api.put(`/learning/collections/${cid}/resources/reorder`, { resource_ids: orderedIds });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reorder failed");
            setDetails((d) => { const n = { ...d }; delete n[cid]; return n; });
            await loadCollection(cid);
        }
    };
    const moveSibling = (siblings, id, delta) => {
        const ids = siblings.map((s) => s.id);
        const from = ids.indexOf(id);
        if (from === -1) return null;
        const to = from + delta;
        if (to < 0 || to > ids.length - 1) return null;
        ids.splice(from, 1);
        ids.splice(to, 0, id);
        return ids;
    };

    /* Auto-expand removed — the tree opens fully collapsed and only
     * expands the nodes the user actively clicks on (NotebookLM
     * behaviour). Details for a collection are fetched on the first
     * expand via `toggle()`, not up-front. */

    const toggle = async (key, cid) => {
        setExpanded((e) => ({ ...e, [key]: !e[key] }));
        if (cid && !details[cid]) await loadCollection(cid);
    };

    const exportPNG = async () => {
        if (!canvasRef.current) return;
        try {
            toast.info("Rendering mind map…");
            const el = canvasRef.current;
            // Capture the inner tree so `overflow: auto` on the canvas
            // element doesn't clip wider layouts. Temporarily disable
            // overflow so html2canvas walks the full DOM subtree.
            const tree = el.querySelector(".lh-c2-mm-htree") || el;
            const prevOverflow = el.style.overflow;
            const prevWidth = el.style.width;
            const prevHeight = el.style.height;
            el.style.overflow = "visible";
            el.style.width = `${tree.scrollWidth + 60}px`;
            el.style.height = `${tree.scrollHeight + 60}px`;
            // Give the browser one frame so measurement + repaint apply
            // before html2canvas snapshots the DOM.
            await new Promise((r) => requestAnimationFrame(r));
            const fullW = Math.max(tree.scrollWidth + 60, el.scrollWidth);
            const fullH = Math.max(tree.scrollHeight + 60, el.scrollHeight);
            let canvas;
            try {
                canvas = await html2canvas(el, {
                    backgroundColor: "#0E1420",
                    scale: 2,
                    logging: false,
                    useCORS: true,
                    width: fullW,
                    height: fullH,
                    windowWidth: Math.max(fullW, window.innerWidth),
                    windowHeight: Math.max(fullH, window.innerHeight),
                    scrollX: 0,
                    scrollY: 0,
                });
            } finally {
                el.style.overflow = prevOverflow;
                el.style.width = prevWidth;
                el.style.height = prevHeight;
            }
            const link = document.createElement("a");
            link.download = `learning-mindmap-${scopedDeptName.replace(/\s+/g, "-").toLowerCase()}-${new Date().toISOString().slice(0, 10)}.png`;
            link.href = canvas.toDataURL("image/png");
            link.click();
            toast.success("Mind map exported");
        } catch (e) {
            toast.error("Export failed — try again");
        }
    };

    // Group by department
    const byDept = useMemo(() => {
        const m = {};
        for (const c of scopedColls) {
            const key = c.owner_department_id || "unassigned";
            const label = c.owner_department_name || "Unassigned";
            (m[key] = m[key] || { id: c.owner_department_id, name: label, collections: [] }).collections.push(c);
        }
        return Object.values(m).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [scopedColls]);

    // ── Horizontal tree renderers.  Each node is a pill; if it has
    //    children they're stacked in a column to the RIGHT of the pill
    //    (NotebookLM-style). The tree flows left-to-right regardless of
    //    depth. Connectors are drawn purely with CSS::before / ::after
    //    inside the child column.
    const renderFolderNodesH = (cid, parentId, collection) => {
        const bundle = details[cid];
        if (!bundle) return null;
        const kids = bundle.folders.filter((f) => (f.parent_folder_id || null) === (parentId || null));
        const canManage = !!(collection && (canManageCollection ? canManageCollection(collection) : true));
        return kids.map((f, idx) => {
            const key = `f-${f.id}`;
            const open = !!expanded[key];
            const inside = bundle.resources.filter((r) => r.folder_id === f.id);
            const subs = bundle.folders.filter((x) => x.parent_folder_id === f.id);
            const hasChildren = subs.length + inside.length > 0;
            const canUp = canManage && idx > 0;
            const canDown = canManage && idx < kids.length - 1;
            const moveFolder = (delta) => {
                const ids = moveSibling(kids, f.id, delta);
                if (ids) reorderFoldersInBundle(cid, parentId || null, ids);
            };
            return (
                <div key={f.id} className="lh-c2-mm-h-node">
                    <div className="lh-c2-mm-pill-wrap">
                        {canManage && (
                            <div className="lh-c2-mm-arrows" onClick={(e) => e.stopPropagation()}>
                                <button
                                    type="button"
                                    className="lh-c2-mm-arrow-btn"
                                    disabled={!canUp}
                                    title="Move up"
                                    aria-label="Move up"
                                    onClick={(e) => { e.stopPropagation(); moveFolder(-1); }}
                                    data-testid={`lh-c2-mm-folder-up-${f.id}`}
                                ><ChevronUp size={10} /></button>
                                <button
                                    type="button"
                                    className="lh-c2-mm-arrow-btn"
                                    disabled={!canDown}
                                    title="Move down"
                                    aria-label="Move down"
                                    onClick={(e) => { e.stopPropagation(); moveFolder(+1); }}
                                    data-testid={`lh-c2-mm-folder-down-${f.id}`}
                                ><ChevronDown size={10} /></button>
                            </div>
                        )}
                        <button
                            className="lh-c2-mm-h-pill"
                            onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                            data-testid={`lh-c2-mm-folder-${f.id}`}
                        >
                            📁 {f.name}
                            {hasChildren && <span className="lh-c2-mm-h-count">{inside.length + subs.length}</span>}
                            {hasChildren && <ChevronRight size={11} className={`lh-c2-mm-h-chev ${open ? "open" : ""}`} />}
                            {onOpenCollection && collection && (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    className="lh-c2-mm-open"
                                    title="Open this collection"
                                    aria-label="Open this collection"
                                    onClick={(e) => { e.stopPropagation(); onOpenCollection(collection); }}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onOpenCollection(collection); } }}
                                    data-testid={`lh-c2-mm-folder-open-${f.id}`}
                                ><ExternalLink size={11} /></span>
                            )}
                        </button>
                    </div>
                    {open && hasChildren && (
                        <HChildren tick={tick}>
                            {renderFolderNodesH(cid, f.id, collection)}
                            {inside.map((r, ri) => {
                                const canRUp = canManage && ri > 0;
                                const canRDown = canManage && ri < inside.length - 1;
                                const moveRes = (delta) => {
                                    const ids = moveSibling(inside, r.id, delta);
                                    if (ids) reorderResourcesInBundle(cid, f.id, ids);
                                };
                                return (
                                    <div key={r.id} className="lh-c2-mm-h-node">
                                        <div className="lh-c2-mm-pill-wrap">
                                            {canManage && (
                                                <div className="lh-c2-mm-arrows" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        type="button"
                                                        className="lh-c2-mm-arrow-btn"
                                                        disabled={!canRUp}
                                                        title="Move up"
                                                        aria-label="Move up"
                                                        onClick={(e) => { e.stopPropagation(); moveRes(-1); }}
                                                        data-testid={`lh-c2-mm-res-up-${r.id}`}
                                                    ><ChevronUp size={10} /></button>
                                                    <button
                                                        type="button"
                                                        className="lh-c2-mm-arrow-btn"
                                                        disabled={!canRDown}
                                                        title="Move down"
                                                        aria-label="Move down"
                                                        onClick={(e) => { e.stopPropagation(); moveRes(+1); }}
                                                        data-testid={`lh-c2-mm-res-down-${r.id}`}
                                                    ><ChevronDown size={10} /></button>
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                className="lh-c2-mm-h-pill leaf clickable"
                                                onClick={() => onOpenResource?.(r, collection)}
                                                data-testid={`lh-c2-mm-res-${r.id}`}
                                            >📄 {r.name}</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </HChildren>
                    )}
                </div>
            );
        });
    };

    return (
        <div className="lh-c2-mm-shell" data-testid="lh-c2-mindmap-shell">
            <div className="lh-c2-mm-topbar">
                <div>
                    <div className="lh-c2-mm-title">
                        <GitBranch size={20} /> Learning Mind Map
                        {myDeptId && deptId === myDeptId && (
                            <span className="lh-c2-mm-mydept-pill" data-testid="lh-c2-mm-mydept-pill">
                                <Star size={11} /> MY DEPARTMENT
                            </span>
                        )}
                    </div>
                    <div className="lh-c2-mm-sub">Explore how collections, folders and resources connect across departments.</div>
                </div>
                <div className="lh-c2-mm-controls">
                    <select
                        className="lh-c2-mm-select"
                        value={deptId || ""}
                        onChange={(e) => setDeptId(e.target.value || null)}
                        data-testid="lh-c2-mm-select"
                    >
                        <option value="">All departments</option>
                        {myDeptId && (depts || []).some((d) => d.id === myDeptId) && (
                            <option value={myDeptId}>★ My Department — {(depts || []).find((d) => d.id === myDeptId)?.name}</option>
                        )}
                        {(depts || []).filter((d) => d.id !== myDeptId).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                    <button className="lh-c2-mm-png" onClick={exportPNG} data-testid="lh-c2-mm-png">
                        <Download size={13} /> Export PNG
                    </button>
                </div>
            </div>
            <div className="lh-c2-mm-canvas" ref={canvasRef} data-testid="lh-c2-mm-canvas">
                <div className="lh-c2-mm-htree">
                    {byDept.length === 0 && (
                        <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, padding: 20 }}>No collections in this scope yet.</div>
                    )}
                    {byDept.map((d) => {
                        const dkey = `d-${d.id || "u"}`;
                        const dopen = !!expanded[dkey];
                        return (
                            <div key={dkey} className="lh-c2-mm-h-node">
                                <button
                                    className="lh-c2-mm-h-pill root"
                                    onClick={() => setExpanded((e) => ({ ...e, [dkey]: !e[dkey] }))}
                                    data-testid={`lh-c2-mm-dept-${d.id || "u"}`}
                                >
                                    🏢 {d.name}
                                    <span className="lh-c2-mm-h-count">{d.collections.length}</span>
                                    <ChevronRight size={11} className={`lh-c2-mm-h-chev ${dopen ? "open" : ""}`} />
                                </button>
                                {dopen && (
                                    <HChildren tick={tick}>
                                        {d.collections.map((c, ci) => {
                                            const ckey = `c-${c.id}`;
                                            const copen = !!expanded[ckey];
                                            const bundle = details[c.id];
                                            const rootFolders = bundle?.folders.filter((f) => !f.parent_folder_id) || [];
                                            const looseRes = bundle?.resources.filter((r) => !r.folder_id) || [];
                                            const hasChildren = rootFolders.length + looseRes.length > 0;
                                            const canManage = canManageCollection ? canManageCollection(c) : true;
                                            const canCUp = canManage && ci > 0;
                                            const canCDown = canManage && ci < d.collections.length - 1;
                                            const moveColl = (delta) => {
                                                // The shelf we reorder against is the current dept's
                                                // list of collections — same as the grid view uses.
                                                if (!onReorderCollections) return;
                                                const to = ci + delta;
                                                if (to < 0 || to > d.collections.length - 1) return;
                                                onReorderCollections(d.collections, ci, to);
                                            };
                                            return (
                                                <div key={c.id} className="lh-c2-mm-h-node">
                                                    <div className="lh-c2-mm-pill-wrap">
                                                        {canManage && onReorderCollections && (
                                                            <div className="lh-c2-mm-arrows" onClick={(e) => e.stopPropagation()}>
                                                                <button
                                                                    type="button"
                                                                    className="lh-c2-mm-arrow-btn"
                                                                    disabled={!canCUp}
                                                                    title="Move up"
                                                                    aria-label="Move up"
                                                                    onClick={(e) => { e.stopPropagation(); moveColl(-1); }}
                                                                    data-testid={`lh-c2-mm-coll-up-${c.id}`}
                                                                ><ChevronUp size={10} /></button>
                                                                <button
                                                                    type="button"
                                                                    className="lh-c2-mm-arrow-btn"
                                                                    disabled={!canCDown}
                                                                    title="Move down"
                                                                    aria-label="Move down"
                                                                    onClick={(e) => { e.stopPropagation(); moveColl(+1); }}
                                                                    data-testid={`lh-c2-mm-coll-down-${c.id}`}
                                                                ><ChevronDown size={10} /></button>
                                                            </div>
                                                        )}
                                                        <button
                                                            className="lh-c2-mm-h-pill"
                                                            onClick={() => toggle(ckey, c.id)}
                                                            data-testid={`lh-c2-mm-coll-${c.id}`}
                                                        >
                                                            📚 {c.name}
                                                            <span className="lh-c2-mm-h-count">{c.resource_count || 0}</span>
                                                            <ChevronRight size={11} className={`lh-c2-mm-h-chev ${copen ? "open" : ""}`} />
                                                            {onOpenCollection && (
                                                                <span
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    className="lh-c2-mm-open"
                                                                    title="Open this collection"
                                                                    aria-label="Open this collection"
                                                                    onClick={(e) => { e.stopPropagation(); onOpenCollection(c); }}
                                                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onOpenCollection(c); } }}
                                                                    data-testid={`lh-c2-mm-coll-open-${c.id}`}
                                                                ><ExternalLink size={11} /></span>
                                                            )}
                                                        </button>
                                                    </div>
                                                    {copen && hasChildren && (
                                                        <HChildren tick={tick}>
                                                            {renderFolderNodesH(c.id, null, c)}
                                                            {looseRes.map((r, ri) => {
                                                                const canRUp = canManage && ri > 0;
                                                                const canRDown = canManage && ri < looseRes.length - 1;
                                                                const moveRes = (delta) => {
                                                                    const ids = moveSibling(looseRes, r.id, delta);
                                                                    if (ids) reorderResourcesInBundle(c.id, null, ids);
                                                                };
                                                                return (
                                                                    <div key={r.id} className="lh-c2-mm-h-node">
                                                                        <div className="lh-c2-mm-pill-wrap">
                                                                            {canManage && (
                                                                                <div className="lh-c2-mm-arrows" onClick={(e) => e.stopPropagation()}>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="lh-c2-mm-arrow-btn"
                                                                                        disabled={!canRUp}
                                                                                        title="Move up"
                                                                                        aria-label="Move up"
                                                                                        onClick={(e) => { e.stopPropagation(); moveRes(-1); }}
                                                                                        data-testid={`lh-c2-mm-res-up-${r.id}`}
                                                                                    ><ChevronUp size={10} /></button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="lh-c2-mm-arrow-btn"
                                                                                        disabled={!canRDown}
                                                                                        title="Move down"
                                                                                        aria-label="Move down"
                                                                                        onClick={(e) => { e.stopPropagation(); moveRes(+1); }}
                                                                                        data-testid={`lh-c2-mm-res-down-${r.id}`}
                                                                                    ><ChevronDown size={10} /></button>
                                                                                </div>
                                                                            )}
                                                                            <button
                                                                                type="button"
                                                                                className="lh-c2-mm-h-pill leaf clickable"
                                                                                onClick={() => onOpenResource?.(r, c)}
                                                                                data-testid={`lh-c2-mm-res-${r.id}`}
                                                                            >📄 {r.name}</button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </HChildren>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </HChildren>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Concept2MindMap;
